import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { HowToJsonLd } from '../components/HowToJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import { ArrowRight, Calculator, Drop, Sparkle, Wrench, Plant, Heart } from '@phosphor-icons/react';

/**
 * /methodology/probe-driven-irrigation — public open-methodology page.
 *
 * Mirrors the formula at /app/docs/probe-driven-irrigation-methodology.md.
 * Anyone can copy the math and rebuild it in Excel; we charge for the
 * 24/7 cron + auto-fire dispatch + receipts.
 */

const StepCard = ({ n, title, body, formula }) => (
  <Card className="border border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30">
    <CardContent className="p-6">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-3xl font-black text-emerald-700">{n}</span>
        <h3 className="text-lg font-bold text-zinc-900">{title}</h3>
      </div>
      <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{body}</p>
      {formula && (
        <pre
          className="mt-3 rounded-lg bg-zinc-900 p-3 text-xs text-emerald-300 font-mono overflow-x-auto"
          data-testid={`methodology-formula-step-${n}`}
        >
          {formula}
        </pre>
      )}
    </CardContent>
  </Card>
);

export default function ProbeDrivenIrrigationPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white"
      data-testid="methodology-probe-driven-page"
    >
      <SEO
        title="Probe-Driven Irrigation Methodology — TurfPulse"
        description="The exact 5-step formula TurfPulse uses to translate a soil probe reading into precise irrigation runtime. Open methodology, citable sources, DIY-able in Excel."
        canonical="https://getturfpulse.com/methodology/probe-driven-irrigation"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Methodology', path: '/methodology/probe-driven-irrigation' },
          { label: 'Probe-Driven Irrigation', path: '/methodology/probe-driven-irrigation' },
        ]}
      />
      <HowToJsonLd
        name="How to translate a soil probe reading into precise irrigation runtime"
        description="The 5-step formula TurfPulse uses to convert a soil moisture probe's SVWC reading into the exact minutes of zone runtime needed."
        totalTime="PT5M"
        steps={[
          { name: 'Pick a target SVWC band', text: 'For Bermuda, target 30% volumetric water content. That sits safely above TAMU\'s 12% drought floor and inside the OPTIMAL bucket band.' },
          { name: 'Compute the deficit', text: 'deficit_svwc_pct = max(0, target_svwc_pct − current_svwc_pct). Example: probe = 22%, target = 30% → deficit = 8%.' },
          { name: 'Convert volumetric % to inches', text: 'SVWC is already volumetric — multiply by the effective root zone depth in inches. For Bermuda, TAMU pegs the effective root zone at 6 inches.' },
          { name: 'Cap at 0.75 inches per session', text: 'Anything more either ponds (clay) or drains below the root zone (sand). Both wasted. This is the agronomic safe-runoff ceiling per IA SWAT testing protocols.' },
          { name: 'Convert inches to minutes via PR × DU', text: 'effective_pr_inhr = nozzle_inhr × du. raw_minutes = (capped_in / effective_pr_inhr) × 60. Clamp to [5 min, 60 min] for sanity.' },
        ]}
      />
      <FaqJsonLd items={[
        { q: 'What is SVWC?', a: 'SVWC stands for Soil Volumetric Water Content — the percentage of soil volume occupied by water. A consumer soil moisture probe reads this value directly. Target ranges for healthy turf sit between 28% and 32%.' },
        { q: 'How accurate is probe-driven irrigation versus weather-based smart controllers?', a: 'A probe reads what is actually in your soil right now; a weather-based controller estimates evapotranspiration from forecast data and assumes uniform soil. In typical residential lawns, probe-driven runtime adjustments save 20-40% more water than stock weather intelligence while maintaining or improving turf quality.' },
        { q: 'Do I need TurfPulse to run probe-driven irrigation?', a: 'No. The 5-step formula on this page works in any spreadsheet. TurfPulse runs the math 24/7, ties it to a Rachio dispatcher, and surfaces receipts — but the methodology itself is open. DIY it yourself, or pay us to run it.' },
        { q: 'What soil moisture probe should I buy?', a: 'For most homeowners, the Ecowitt WH51 (moisture-only) or the newer WH52 (3-in-1: moisture + temperature + EC) on a GW1100 gateway is the cleanest path. See the gear list below for the full kit and affiliate links.' },
        { q: 'How does the TAMU 12% drought floor work?', a: 'Texas A&M AgriLife research (Wherley et al.) found that bermudagrass on fine sandy loam fairways enters visible drought stress when SVWC drops below approximately 12%. TurfPulse uses this as a hard override threshold — when the DRY-zone probe drops below 12%, the controller fires regardless of weather skip rules (except freeze and city blackout windows, which always win).' },
      ]} />

      {/* Hero */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
            <Sparkle size={14} weight="fill" /> OPEN METHODOLOGY
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-zinc-900 sm:text-5xl">
            Your probe knows. Your sprinkler knows. The runtime is just division.
          </h1>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            TurfPulse translates soil-moisture readings into the{' '}
            <strong>exact minutes of runtime</strong> needed — per zone, per
            fire. The math isn't proprietary. It's the same FAO-56 + Christiansen
            framework university extensions have used for decades. We just wired
            it to a Rachio.
          </p>
          <p className="mt-3 text-base text-zinc-600">
            This page documents the formula in full so anyone can rebuild it.
            Copy it. Run it in Excel. Or let TurfPulse run it 24/7 for the
            price of a bag of fertilizer per month.
          </p>
        </div>
      </section>

      {/* The 5 steps */}
      <section className="mx-auto max-w-4xl px-6 py-16">
        <h2 className="text-3xl font-black text-zinc-900">The 5-step formula</h2>
        <p className="mt-2 text-zinc-700">
          Each step has a citation. Each step is independently testable. This is
          the entire engine.
        </p>

        <div className="mt-8 space-y-5">
          <StepCard
            n="1"
            title="Pick a target SVWC band"
            body="SVWC = Soil Volumetric Water Content (what the probe reads). For Bermuda we target 30%. That's safely above TAMU's 12% drought floor and inside the OPTIMAL bucket band."
            formula={`# Bermuda default
target_svwc_pct = 30

# TAMU AgriLife: bermuda fairways enter
# visible drought stress below 12% on
# fine sandy loam (Wherley et al.)`}
          />
          <StepCard
            n="2"
            title="Compute the deficit"
            body="How far below target is the probe right now? If at-or-above target, we don't fire — micro-fires waste water and disrupt root depth."
            formula={`deficit_svwc_pct = max(0, target_svwc_pct − current_svwc_pct)

# Example: probe = 22%, target = 30%
# → deficit = 8%`}
          />
          <StepCard
            n="3"
            title="Convert volumetric % to inches"
            body="SVWC is already volumetric — multiply by the effective root zone depth in inches. For Bermuda, TAMU pegs the effective root zone at 6&quot; (80%+ of bermuda roots live there)."
            formula={`deficit_in = (deficit_svwc_pct / 100) × root_zone_in

# 0.08 × 6 = 0.48"`}
          />
          <StepCard
            n="4"
            title="Cap at 0.75″ per session"
            body="Anything more either ponds (clay) or drains below the root zone (sand). Both wasted. This is the agronomic safe-runoff ceiling per IA SWAT testing protocols."
            formula={`capped_in = min(deficit_in, 0.75)`}
          />
          <StepCard
            n="5"
            title="Convert inches to minutes via PR × DU"
            body={
              <>
                <strong>PR</strong> = your nozzle's precipitation rate (in/hr).
                <strong> DU</strong> = Christiansen Distribution Uniformity
                (~0.75 for residential pop-ups). Clamp to [5 min, 60 min] for
                sanity.
              </>
            }
            formula={`effective_pr_inhr = nozzle_inhr × du
raw_minutes       = (capped_in / effective_pr_inhr) × 60
runtime_minutes   = max(5, min(60, raw_minutes))

# Example: 0.48" / (0.5 × 0.75) × 60
#        = 0.48 / 0.375 × 60
#        = 76.8 → clamped to 60`}
          />
        </div>
      </section>

      {/* Worked example */}
      <section className="border-y border-zinc-200 bg-zinc-50/60">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <h2 className="text-3xl font-black text-zinc-900">Worked example</h2>
          <p className="mt-2 text-zinc-700">
            Round Rock, TX. Bermuda. CH2 ("trigger zone") reads <strong>22%</strong>{' '}
            SVWC at 5:30 AM predawn. Zone PR is <strong>0.5 in/hr</strong> (rotor).
            User typed "30 min" in their schedule.
          </p>
          <Card className="mt-6">
            <CardContent className="p-0">
              <table
                className="w-full text-sm"
                data-testid="methodology-worked-example"
              >
                <tbody className="divide-y divide-zinc-200">
                  {[
                    ['Step 1', 'target_svwc', '30 %', 'TurfPulse Bermuda default'],
                    ['Step 2', 'deficit_svwc', '30 − 22 = 8 %', 'Step 2 formula'],
                    ['Step 3', 'deficit_in', '0.08 × 6 = 0.48 in', '6 in root zone'],
                    ['Step 4', 'capped_in', 'min(0.48, 0.75) = 0.48 in', 'Below cap'],
                    ['Step 5', 'effective_pr', '0.5 × 0.75 = 0.375 in/hr', 'PR × DU'],
                    ['Step 5', 'raw_minutes', '0.48 / 0.375 × 60 = 76.8', 'Inches → min'],
                    ['Step 5', 'runtime_minutes', '60 min', 'Clamped to MAX_RUN_MIN'],
                  ].map(([s, k, v, note], i) => (
                    <tr key={i} className={i === 6 ? 'bg-emerald-50/60 font-bold' : ''}>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-500">{s}</td>
                      <td className="px-4 py-2 font-mono text-zinc-900">{k}</td>
                      <td className="px-4 py-2 font-mono text-zinc-700">{v}</td>
                      <td className="px-4 py-2 text-zinc-600">{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="mt-4 text-zinc-700">
            TurfPulse fires Zone 2 for <strong>60 minutes</strong> instead of the
            typed 30, because the probe says the deficit warrants it. Three days
            later when the same probe reads 28%, TurfPulse fires Zone 2 for{' '}
            <strong>~10 minutes</strong>. Same total weekly water — distributed
            where the soil asks for it.
          </p>
        </div>
      </section>

      {/* DIY Excel */}
      <section className="mx-auto max-w-4xl px-6 py-16">
        <div className="flex items-center gap-2">
          <Calculator size={28} className="text-emerald-700" weight="duotone" />
          <h2 className="text-3xl font-black text-zinc-900">DIY this in Excel</h2>
        </div>
        <p className="mt-2 text-zinc-700">
          Copy this worksheet. Punch in your probe reading at B1. Cell B11 is
          your runtime.
        </p>
        <Card className="mt-6">
          <CardContent className="p-0">
            <table
              className="w-full text-sm"
              data-testid="methodology-excel-worksheet"
            >
              <thead className="bg-zinc-100 text-xs uppercase text-zinc-600">
                <tr>
                  <th className="px-4 py-2 text-left">Cell</th>
                  <th className="px-4 py-2 text-left">Label</th>
                  <th className="px-4 py-2 text-left">Formula / Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 font-mono text-xs">
                {[
                  ['B1',  'current_svwc_pct (probe)',  'enter from probe'],
                  ['B2',  'target_svwc_pct',           '30'],
                  ['B3',  'root_zone_in',              '6'],
                  ['B4',  'nozzle_inhr (PR)',          '0.5'],
                  ['B5',  'du',                        '0.75'],
                  ['B6',  'deficit_svwc_pct',          '=MAX(0, B2-B1)'],
                  ['B7',  'deficit_in',                '=(B6/100)*B3'],
                  ['B8',  'capped_in',                 '=MIN(B7, 0.75)'],
                  ['B9',  'effective_pr_inhr',         '=B4*B5'],
                  ['B10', 'raw_minutes',               '=(B8/B9)*60'],
                  ['B11', 'runtime_minutes (final)',   '=MAX(5, MIN(60, B10))'],
                ].map(([c, l, f]) => (
                  <tr key={c}>
                    <td className="px-4 py-2 text-zinc-500">{c}</td>
                    <td className="px-4 py-2 text-zinc-900">{l}</td>
                    <td className="px-4 py-2 text-emerald-700">{f}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Sources */}
      <section className="border-t border-zinc-200 bg-zinc-900 text-zinc-100" data-testid="methodology-sources">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-2xl font-black">Sources & citations</h2>
            <Link
              to="/credits"
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-800"
              data-testid="methodology-credits-link"
            >
              Full data sources page <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <p className="mt-2 text-sm text-zinc-300">
            Every threshold on this page traces back to peer-reviewed work.
            Click through for the DOI.
          </p>
          <ul className="mt-6 space-y-4 text-sm leading-relaxed">
            <li data-testid="cite-fao56">
              <strong>FAO-56 Penman-Monteith</strong> — Allen, R.G., Pereira, L.S., Raes, D., &amp; Smith, M. (1998).{' '}
              <em>Crop Evapotranspiration: Guidelines for Computing Crop Water Requirements.</em>{' '}
              FAO Irrigation and Drainage Paper No. 56.{' '}
              <a
                href="https://www.fao.org/4/x0490e/x0490e00.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                fao.org/4/x0490e
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: ET₀ computation in the daily water budget.
              </div>
            </li>
            <li data-testid="cite-wherley">
              <strong>Wherley et al. (TAMU AgriLife Extension)</strong> — soil-moisture-based deficit
              irrigation thresholds for warm-season turfgrass cultivars on USGA-spec sand profiles
              (2014–2020 series).{' '}
              <a
                href="https://aggieturf.tamu.edu/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                aggieturf.tamu.edu
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: 12% SVWC drought-floor override on bermudagrass.
              </div>
            </li>
            <li data-testid="cite-smith-kerns">
              <strong>Smith-Kerns Dollar-Spot Model</strong> — Smith, D.L., Kerns, J.P., et al.
              (2018). <em>Development and validation of a weather-based warning system to advise
              fungicide applications to control dollar spot on turfgrass.</em>{' '}
              PLOS ONE 13(3): e0194216. DOI:{' '}
              <a
                href="https://doi.org/10.1371/journal.pone.0194216"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                10.1371/journal.pone.0194216
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: Dollar-spot pressure score (5-day moving avg RH × daily mean temp logistic regression). 20% spray threshold cuts fungicide use up to 30%.
              </div>
            </li>
            <li data-testid="cite-christiansen">
              <strong>Christiansen 1942</strong> — Christiansen, J.E.{' '}
              <em>Hydraulics of Sprinkling Systems for Irrigation.</em>{' '}
              California Agricultural Experiment Station Bulletin 670.{' '}
              <a
                href="https://oac.cdlib.org/findaid/ark:/13030/c8k64nzh/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                UC Bulletin 670
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: Distribution Uniformity (DU) coefficient in Step 5 — converts nominal nozzle PR to effective PR.
              </div>
            </li>
            <li data-testid="cite-dewpoint-aps">
              <strong>Dewpoint band &amp; leaf-wetness research</strong> — Latin, R., et al. (2021).{' '}
              <em>Dollar Spot of Turfgrass: Biology, Epidemiology, and Management.</em>{' '}
              Phytopathology, APS Journals.{' '}
              <a
                href="https://apsjournals.apsnet.org/doi/10.1094/PHYTO-11-20-0505-RVW"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                APS review
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: Dewpoint 60–70°F advisory band on the disease-pressure tile (prolonged leaf-wetness epidemiology).
              </div>
            </li>
            <li data-testid="cite-ia-swat">
              <strong>Irrigation Association SWAT</strong> — Smart Water Application Technologies
              testing protocols.{' '}
              <a
                href="https://www.irrigation.org/IA/Resources/SWAT/IA/Resources/SWAT.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                irrigation.org/SWAT
              </a>
              <div className="text-xs text-zinc-400 mt-1">
                Used for: 0.75″/session safe-runoff cap in Step 4.
              </div>
            </li>
            <li data-testid="cite-mu-uga">
              <strong>Extension references</strong> — University of Missouri IPM1029{' '}
              (
              <a
                href="https://extension.missouri.edu/publications/ipm1029"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                MU IPM1029
              </a>
              ) and University of Georgia Circular 891 (
              <a
                href="https://fieldreport.caes.uga.edu/wp-content/uploads/2025/08/C-891_7.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 underline"
              >
                UGA C-891
              </a>
              ).
              <div className="text-xs text-zinc-400 mt-1">
                Used for: Predawn fire bias, dew-removal protocol, deep-and-infrequent watering pattern.
              </div>
            </li>
          </ul>
          <p className="mt-6 text-xs text-zinc-400 italic">
            See the full{' '}
            <Link to="/credits" className="text-emerald-400 underline">
              data sources &amp; credits page
            </Link>{' '}
            for every weather feed, soils dataset, and model TurfPulse uses — with public URLs and licenses.
          </p>
        </div>
      </section>

      {/* ─── Set It Up Yourself ─────────────────────────────── */}
      <section
        className="border-t border-zinc-200 bg-amber-50/40"
        data-testid="methodology-diy-setup"
      >
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="flex items-center gap-2">
            <Wrench size={28} className="text-amber-700" weight="duotone" />
            <h2 className="text-3xl font-black text-zinc-900">
              Set it up yourself
            </h2>
          </div>
          <p className="mt-3 text-zinc-700 leading-relaxed">
            <strong>Honesty check before you click "buy":</strong> probes don't
            talk to Rachio out of the box. Ecowitt sensors broadcast on 915 MHz
            to their own gateway → gateway pushes to Ecowitt's cloud → something
            (TurfPulse, or your own script) reads that cloud and tells Rachio
            what to do. There's a small wiring layer in between. A geek can
            figure it out in an afternoon. A homeowner who's never seen an API
            key is going to want a guide. <em>This is that guide.</em>
          </p>

          {/* Difficulty meter */}
          <div
            className="mt-6 rounded-lg border border-amber-300 bg-white p-4"
            data-testid="methodology-difficulty"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
              Difficulty meter
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <span className="font-mono text-zinc-500">Geek mode:</span>{' '}
                <span className="font-bold text-emerald-700">4/10</span>
                <span className="block text-xs text-zinc-600">
                  One afternoon. You've used Postman before.
                </span>
              </div>
              <div>
                <span className="font-mono text-zinc-500">Homeowner DIY:</span>{' '}
                <span className="font-bold text-amber-700">7/10</span>
                <span className="block text-xs text-zinc-600">
                  A weekend. With this guide. With patience.
                </span>
              </div>
              <div>
                <span className="font-mono text-zinc-500">Skip the headache:</span>{' '}
                <span className="font-bold text-sky-700">1/10</span>
                <span className="block text-xs text-zinc-600">
                  Sign up for TurfPulse. We do the wiring.
                </span>
              </div>
            </div>
          </div>

          {/* Gear list with affiliate links */}
          <h3 className="mt-10 text-2xl font-black text-zinc-900">
            The gear (with my affiliate links)
          </h3>
          <p className="mt-2 text-sm text-zinc-600 italic">
            Same hardware I run on my own lawn. Buying through these links costs
            you nothing extra and helps me keep the lights on at TurfPulse —
            which is how this guide stays free instead of behind a paywall.
            Amazon Associates disclosure applies.
          </p>
          <div
            className="mt-5 overflow-hidden rounded-lg border border-zinc-200 bg-white"
            data-testid="methodology-gear-list"
          >
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-xs uppercase text-zinc-600">
                <tr>
                  <th className="px-4 py-2 text-left">Gear</th>
                  <th className="px-4 py-2 text-left">Why</th>
                  <th className="px-4 py-2 text-left">Cost band</th>
                  <th className="px-4 py-2 text-left">Get it</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {[
                  {
                    name: 'Ecowitt GW1100 Wi-Fi Gateway',
                    why: 'The hub. One per house. Receives every probe + station signal and pipes it to Ecowitt cloud.',
                    cost: '$',
                    url: 'https://www.amazon.com/s?k=ecowitt+GW1100+wifi+gateway&tag=turfpulse0d-20',
                    testid: 'gear-gw1100',
                  },
                  {
                    name: 'Ecowitt WH51 Soil Moisture Probes (3-pack)',
                    why: 'The brains of the operation. Bury one per zone trio (DRY / AVG / WET). The combo TurfPulse parses today.',
                    cost: '$$',
                    url: 'https://www.amazon.com/s?k=ecowitt+WH51+soil+moisture+sensor&tag=turfpulse0d-20',
                    testid: 'gear-wh51',
                  },
                  {
                    name: '🆕 Ecowitt WH52 Soil Moisture + Temp + EC (3-in-1, 2026)',
                    why: 'Newer single-probe combo — moisture (0–100%) + temp (−40 to 60°C) + electrical conductivity 0–10,000 μS/cm (catches salt buildup from over-fertilizing or hard water). Replaces the WH51+WN34 pair with one battery, one hole per zone, IP66 entire device, 16 channels per gateway. Pairs with GW1100 (match 915 MHz US version). $29.99 each on Amazon — cheaper than buying WH51+WN34 separately. ✅ TurfPulse parses WH52 payloads natively (moisture + temp + EC bands: deficient/ideal/watch/high). Plug it in, the dashboard fills in.',
                    cost: '$$',
                    url: 'https://www.amazon.com/s?k=ECOWITT+WH52+soil+meter+3-in-1+sensor&tag=turfpulse0d-20',
                    testid: 'gear-wh52',
                  },
                  {
                    name: 'Ecowitt WN34 Soil Temperature Probe (skip if going WH52)',
                    why: 'Drives pre-emergent timing + warm-season green-up alerts. Buy ONLY if you went WH51 — the WH52 already includes temperature.',
                    cost: '$',
                    url: 'https://www.amazon.com/s?k=ecowitt+WN34+soil+temperature&tag=turfpulse0d-20',
                    testid: 'gear-wn34',
                  },
                  {
                    name: 'Rachio 3 Smart Sprinkler Controller (8 or 16 zone)',
                    why: 'The actuator. Anything with the public Rachio API works; the 3rd-gen has the cleanest auth flow.',
                    cost: '$$$',
                    url: 'https://www.amazon.com/s?k=rachio+3+smart+sprinkler+controller+8+zone&tag=turfpulse0d-20',
                    testid: 'gear-rachio',
                  },
                  {
                    name: 'Catch-cup set (12 cups)',
                    why: 'You need to MEASURE your nozzle PR for Step 5 of the formula. Don\'t skip this — guessed PR is worse than no PR.',
                    cost: '$',
                    url: 'https://www.amazon.com/s?k=sprinkler+catch+cup+kit&tag=turfpulse0d-20',
                    testid: 'gear-catchcups',
                  },
                  {
                    name: 'Ecowitt WS90 7-in-1 Weather Station',
                    why: 'NOT REQUIRED. Skip this. The closed-loop runs on soil probes alone — they\'re the only sensor that talks to the dirt your roots actually live in. Forecast data from your local NWS station is already plenty. The WS90 is a "nice-to-have" for backyard weather hobbyists, not a prerequisite.',
                    cost: '$$$ (skip)',
                    url: 'https://www.amazon.com/s?k=ecowitt+WS90+7-in-1+weather+station&tag=turfpulse0d-20',
                    testid: 'gear-ws90',
                  },
                ].map((g) => (
                  <tr key={g.testid}>
                    <td className="px-4 py-3 font-semibold text-zinc-900">{g.name}</td>
                    <td className="px-4 py-3 text-zinc-700">{g.why}</td>
                    <td className="px-4 py-3 font-mono text-zinc-600">{g.cost}</td>
                    <td className="px-4 py-3">
                      <a
                        href={g.url}
                        target="_blank"
                        rel="noopener sponsored noreferrer"
                        data-testid={`methodology-${g.testid}-link`}
                        className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1 text-xs font-bold text-white hover:bg-amber-700"
                      >
                        Amazon <ArrowRight size={12} weight="bold" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-zinc-500 italic">
            FTC disclosure: As an Amazon Associate, TurfPulse earns from
            qualifying purchases. The list above isn't every probe on the
            planet — it's the gear that's actually been buried in my own yard
            for the Memorial Day Showcase, with the part numbers that play
            cleanly with the Ecowitt cloud → TurfPulse → Rachio pipeline.
          </p>

          {/* The 4-step wiring */}
          <h3 className="mt-12 text-2xl font-black text-zinc-900">
            The 4-step wiring (what isn't on the box)
          </h3>
          <div
            className="mt-5 grid gap-4 md:grid-cols-2"
            data-testid="methodology-wiring-steps"
          >
            {[
              {
                n: '1',
                title: 'Bury the probes by convention',
                body: 'Pick a fixed convention before the shovel hits dirt. I use CH1 = DRY zone (worst-draining), CH2 = AVG (representative baseline), CH3 = WET (the over-water guard). Label them with a Sharpie. Never move them. The TurfPulse override fires off CH1 specifically — get that one in your worst spot.',
              },
              {
                n: '2',
                title: 'Pair the gateway, get the API keys',
                body: 'Set up the Ecowitt gateway in the WS View Plus app. Once probes are reporting, generate an Application Key + API Key in the Ecowitt web console (User Center → API). Copy the MAC of your gateway. Three strings. Put them somewhere safe.',
              },
              {
                n: '3',
                title: 'Get a Rachio Personal Access Token',
                body: 'Rachio web app → Account Settings → Get API Key. Generate a Personal Access Token (PAT). This is the credential that lets anything on the internet fire your zones — keep it private. TurfPulse stores it encrypted at rest with Fernet keys.',
              },
              {
                n: '4',
                title: 'Wire them together',
                body: 'You can roll your own glue (a 50-line Python cron job that polls Ecowitt and POSTs to Rachio) or paste your three Ecowitt strings + Rachio PAT into TurfPulse and let the cron loop + Guardian + skip-gates run 24/7. Either path works. Both end at "my sensors told the controller when to water."',
              },
            ].map((s) => (
              <Card key={s.n} className="border-amber-200">
                <CardContent className="p-5">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-2xl font-black text-amber-700">
                      {s.n}
                    </span>
                    <h4 className="text-base font-bold text-zinc-900">{s.title}</h4>
                  </div>
                  <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
                    {s.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── The Summer Test ────────────────────────────────── */}
      <section
        className="border-t border-zinc-200 bg-gradient-to-br from-emerald-50 to-sky-50"
        data-testid="methodology-summer-test"
      >
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="flex items-center gap-2">
            <Plant size={28} className="text-emerald-700" weight="duotone" />
            <h2 className="text-3xl font-black text-zinc-900">
              The summer test — and where the savings go
            </h2>
          </div>
          <p className="mt-3 text-zinc-700 leading-relaxed text-lg">
            A two-week experiment proves nothing. <strong>The real proof is
            lasting the whole summer.</strong> May through October, full Texas
            heat, the controller deciding for itself based on probes — that's
            the test.
          </p>
          <p className="mt-3 text-zinc-700 leading-relaxed">
            Track your municipal water bill from the same months last year.
            Most homeowners running probe-driven irrigation see <strong>20–40%
            water reduction</strong> versus a calendar-fired Rachio with stock
            Weather Intelligence. On a $180/month summer water bill, that's
            $35–$70 saved every month — call it $200+ across the season.
          </p>
          <p className="mt-3 text-zinc-700 leading-relaxed">
            Here's the rule for that money: <strong>it goes back into the
            soil.</strong> Compost. Gypsum. Humic acid. A bag of Essential G.
            Soil amendments compound — better soil holds more water, which lets
            the probes report higher SVWC, which lets the controller fire less,
            which saves more money, which buys more amendments. That's the
            flywheel.
          </p>

          <Card className="mt-6 border-emerald-300">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
                Summer savings → soil amendments calculator (rough)
              </div>
              <table className="mt-3 w-full text-sm">
                <tbody className="divide-y divide-emerald-100">
                  <tr>
                    <td className="py-2 text-zinc-700">Average TX summer water bill</td>
                    <td className="py-2 font-mono text-zinc-900 text-right">
                      $180 / month × 6 = $1,080
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-700">Conservative 25% reduction</td>
                    <td className="py-2 font-mono text-emerald-700 text-right font-bold">
                      −$270 saved
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-700">→ 4 bags of Essential G (humic + biology)</td>
                    <td className="py-2 font-mono text-zinc-700 text-right">~$120</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-700">→ Pelletized gypsum (40 lb × 2)</td>
                    <td className="py-2 font-mono text-zinc-700 text-right">~$50</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-700">→ Compost top-dress for 1 zone</td>
                    <td className="py-2 font-mono text-zinc-700 text-right">~$80</td>
                  </tr>
                  <tr className="bg-emerald-50/60 font-bold">
                    <td className="py-2 text-zinc-900">Net: water bill funded next year's soil work</td>
                    <td className="py-2 font-mono text-emerald-700 text-right">$0 out of pocket</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ─── Founder credit ─────────────────────────────────── */}
      <section
        className="border-t border-zinc-200 bg-zinc-900 text-zinc-100"
        data-testid="methodology-founder-credit"
      >
        <div className="mx-auto max-w-3xl px-6 py-12">
          <div className="flex items-center gap-2">
            <Heart size={22} className="text-rose-400" weight="fill" />
            <h2 className="text-xl font-black">Who put this together</h2>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-zinc-200">
            <strong>Compiled by Derwin</strong> — a regular guy in Round Rock,
            TX who got tired of his Rachio firing through rainstorms and
            skipping during heat waves. He buried three soil probes at sunset,
            watched CH2 dry first, mapped them to zones, and figured out that
            <em> the math is the easy part — getting the probes to actually
            talk to the controller is what nobody tells you about.</em>
          </p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-300">
            This page exists because he built the wiring once, wrote it down,
            and decided that gating it behind a paywall would be a betrayal of
            why he started TurfPulse in the first place. Use the math. Use the
            guide. Buy the gear through his links if you want to say thanks.
            And if your water bill drops this summer — that's him helping you
            save it. Tip your hat.
          </p>
          <p className="mt-3 text-xs italic text-zinc-400">
            "My sensors told the controller when to water." — the headline he
            wrote two weeks before the Memorial Day reveal.
          </p>
        </div>
      </section>

      {/* Fair-price pitch */}
      <section className="mx-auto max-w-4xl px-6 py-16">
        <div className="flex items-center gap-2">
          <Drop size={28} className="text-sky-600" weight="duotone" />
          <h2 className="text-3xl font-black text-zinc-900">The fair-price pitch</h2>
        </div>
        <p className="mt-3 text-zinc-700 leading-relaxed">
          You can absolutely DIY this. The math is here. The probes are about
          $40 each on Amazon (you only need 1–3). You don't need a weather
          station — your local NWS station already covers forecast and ET₀
          inputs. An afternoon of soldering and Python and you've got your
          own little brain.
        </p>
        <p className="mt-3 text-zinc-700 leading-relaxed">
          What TurfPulse charges for is the part that's annoying:
        </p>
        <ul className="mt-3 space-y-2 text-zinc-700 list-disc pl-6">
          <li>
            The 24/7 cron loop that polls Ecowitt every 15 min and tags clean
            predawn / predusk anchors.
          </li>
          <li>
            The Rachio token vault + auto-fire dispatcher with sensor-gated
            <strong> Guardian policy</strong> (no fires on theory, ever).
          </li>
          <li>
            The skip-gate stack — rain, freeze, wind, dewpoint, MAD, city
            blackout — wired to your probes.
          </li>
          <li>
            The <strong>TAMU drought-floor override</strong> that fires anyway
            when CH1 &lt; 12 %.
          </li>
          <li>
            The activity-feed receipt showing every fire & skip with the exact
            agronomic snapshot that drove it.
          </li>
        </ul>
        <p className="mt-5 text-zinc-700 leading-relaxed">
          For a homeowner that's <strong>about $5–8/month</strong>. For an
          enthusiast, $15. The probes pay for themselves in saved water in one
          summer; TurfPulse pays for itself in the second summer when the cron
          catches the 100° week you didn't notice because you were on vacation.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="methodology-conversion-cta">
          {/* Primary: real Inner Circle CTA — drives sign-up directly from the
              methodology read instead of bouncing through /pricing. */}
          <InnerCircleCTA testId="methodology-inner-circle-cta" size="default" />
          <Link to="/pricing">
            <Button
              variant="outline"
              data-testid="methodology-cta-pricing"
            >
              Compare all plans
            </Button>
          </Link>
          <Link to="/water-budget">
            <Button
              variant="outline"
              data-testid="methodology-cta-budget"
            >
              Try the Water Budget calculator
            </Button>
          </Link>
          <Link to="/sensors/placement">
            <Button
              variant="outline"
              data-testid="methodology-cta-placement"
            >
              Map your probes
            </Button>
          </Link>
        </div>
        <p className="mt-8 text-sm text-zinc-500">
          If the math here is enough for you — go build it. We'll cheer you on.
          If you'd rather we run it for you — we're here.
        </p>
      </section>
    </div>
  );
}
